// Seleção múltipla, dois formatos:
//   - `multiSelect`   → "combobox": um <details> que abre uma lista de
//     checkboxes. Para um filtro discreto numa barra (quais hotéis mostrar na
//     matriz de preços).
//   - `painelSelecao` → painel SEMPRE VISÍVEL com busca no topo. Para quando
//     escolher itens é a tarefa central da janela (abrir faturas em massa) —
//     nada abre por cima, nada obriga a rolar o modal.

import { emTitulo } from "../../lib/em-titulo.ts";
import { clear, el, registrarPortalFlutuante, botao } from "../ui.ts";
import { casaBusca } from "./_cadastro.ts";

export interface MultiSelect {
  readonly element: HTMLElement;
  /** Ids marcados. */
  selecionados(): string[];
  /** Marca todos os ids da lista atual. */
  marcarTodos(): void;
  /** Marca exatamente estes ids (os demais desmarcam) — para outro controle
   * da tela, como a legenda do gráfico, mudar a mesma seleção. */
  definirSelecionados(ids: readonly string[]): void;
  /** Atualiza a lista de itens sem recriar o elemento, mantendo a seleção onde aplicável. */
  atualizarItens(novosItens: readonly { id: string; nome: string }[]): void;
}

export function multiSelect(opts: {
  rotulo: string;
  itens: readonly { id: string; nome: string }[];
  /** Começa com tudo marcado (padrão: true). Ignorado se `marcadosPorPadrao` vier. */
  todosPorPadrao?: boolean;
  /** Começa com exatamente estes ids marcados (ex.: editar uma seleção já salva). */
  marcadosPorPadrao?: readonly string[];
  aoMudar?: () => void;
}): MultiSelect {
  let itensAtivos = opts.itens;
  const marcados = new Set<string>(
    opts.marcadosPorPadrao != null
      ? opts.marcadosPorPadrao
      : opts.todosPorPadrao === false
        ? []
        : itensAtivos.map((i) => i.id),
  );

  const resumo = el("span", { class: "ms-resumo" });
  const lista = el("div", { class: "ms-lista" });
  const det = el(
    "details",
    { class: "multi-select" },
    el("summary", {}, resumo),
    lista,
  );

  // A lista abre como portal em <body> (position: fixed, medida a partir de
  // `det`) em vez de `position: absolute` dentro de `.multi-select` — um
  // ancestral com `overflow` (ex.: `.modal-body`/`.modal-box`, achado na
  // tela de Cliente) cortava o combobox no meio, sem conseguir passar do
  // limite do modal. `registrarPortalFlutuante` (ui.ts) avisa a prisão de
  // foco de um modal aberto pra não perder os checkboxes daqui, já que eles
  // saem de dentro de `.modal-box` enquanto abertos.
  let desregistrarPortal: (() => void) | null = null;
  let observadorDesconexao: MutationObserver | null = null;
  // `capture: true` num scroll de `window` recebe o scroll de QUALQUER
  // elemento da página, inclusive o de dentro da própria `.ms-lista` (ela
  // rola por `overflow-y: auto` quando tem muitos itens) — sem filtrar pelo
  // alvo, rolar a lista pra ver os itens de baixo fechava o combobox no
  // meio do gesto.
  const fecharSeRolarOuRedimensionar = (ev?: Event): void => {
    if (ev && ev.target instanceof Node && lista.contains(ev.target)) return;
    if (det.open) det.open = false;
  };
  const posicionarLista = (): void => {
    const r = det.getBoundingClientRect();
    lista.style.position = "fixed";
    lista.style.top = `${r.bottom + 4}px`;
    lista.style.left = `${r.left}px`;
    // Nunca mais estreita que o próprio gatilho — sem descer do mínimo de
    // 200px que `.ms-lista` já usa (CSS), só crescer se `det` for mais largo.
    lista.style.minWidth = `${Math.max(200, r.width)}px`;
  };
  det.addEventListener("toggle", () => {
    if (det.open) {
      posicionarLista();
      document.body.append(lista);
      desregistrarPortal = registrarPortalFlutuante(lista);
      window.addEventListener("scroll", fecharSeRolarOuRedimensionar, true);
      window.addEventListener("resize", fecharSeRolarOuRedimensionar);
      // Se `det` sair do documento sem passar por aqui (tela redesenhada com
      // o combobox ainda aberto, ex.: `render()` de filtro em outra parte da
      // tela) a lista portada em `<body>` nunca seria recolhida sozinha —
      // força o fechamento assim que isso acontece, reaproveitando a mesma
      // limpeza do ramo abaixo.
      observadorDesconexao = new MutationObserver(() => {
        if (!det.isConnected) det.open = false;
      });
      observadorDesconexao.observe(document.body, { childList: true, subtree: true });
    } else {
      lista.style.position = "";
      lista.style.top = "";
      lista.style.left = "";
      lista.style.minWidth = "";
      det.append(lista);
      desregistrarPortal?.();
      desregistrarPortal = null;
      window.removeEventListener("scroll", fecharSeRolarOuRedimensionar, true);
      window.removeEventListener("resize", fecharSeRolarOuRedimensionar);
      observadorDesconexao?.disconnect();
      observadorDesconexao = null;
    }
  });

  // "2 de 3" obriga abrir o combobox pra lembrar o que está filtrado —
  // mostra o(s) nome(s) reais quando cabem, senão o primeiro + quantos
  // faltam (nunca a contagem crua sozinha, que não diz nada sem abrir).
  const atualizarResumo = (): void => {
    const nomes = itensAtivos.filter((i) => marcados.has(i.id)).map((i) => i.nome);
    const n = nomes.length;
    resumo.textContent =
      n === 0
        ? "Nenhum"
        : n === itensAtivos.length
          ? `Todos (${n})`
          : n <= 2
            ? nomes.join(", ")
            : `${nomes[0]} +${n - 1}`;
    // "Nenhum" é ausência de valor: cor de texto secundário (docs/design/ui.md).
    resumo.classList.toggle("ms-vazio", n === 0);
  };

  const desenhar = (): void => {
    clear(lista);
    const todos = botao("link", {}, "Marcar Todos");
    const nenhum = botao("link", {}, "Nenhum");
    todos.addEventListener("click", () => {
      for (const i of itensAtivos) marcados.add(i.id);
      desenhar();
      atualizarResumo();
      opts.aoMudar?.();
    });
    nenhum.addEventListener("click", () => {
      marcados.clear();
      desenhar();
      atualizarResumo();
      opts.aoMudar?.();
    });
    lista.append(el("div", { class: "ms-acoes" }, todos, nenhum));
    for (const i of itensAtivos) {
      const cb = el("input", { type: "checkbox" }) as HTMLInputElement;
      cb.checked = marcados.has(i.id);
      cb.addEventListener("change", () => {
        if (cb.checked) marcados.add(i.id);
        else marcados.delete(i.id);
        atualizarResumo();
        opts.aoMudar?.();
      });
      lista.append(el("label", { class: "ms-item" }, cb, ` ${i.nome}`));
    }
  };
  desenhar();
  atualizarResumo();

  return {
    // <div>, não <label>: envolver muitos checkboxes num <label> faria o clique
    // em qualquer lugar alternar o primeiro deles.
    element: el("div", { class: "filtro" }, el("span", {}, emTitulo(opts.rotulo)), det),
    selecionados: () =>
      itensAtivos.map((i) => i.id).filter((id) => marcados.has(id)),
    marcarTodos: () => {
      for (const i of itensAtivos) marcados.add(i.id);
      desenhar();
      atualizarResumo();
    },
    definirSelecionados: (ids) => {
      marcados.clear();
      for (const id of ids) marcados.add(id);
      desenhar();
      atualizarResumo();
    },
    atualizarItens: (novosItens) => {
      itensAtivos = novosItens;
      desenhar();
      atualizarResumo();
    },
  };
}

export interface PainelSelecao {
  readonly element: HTMLElement;
  /** Ids marcados. */
  selecionados(): string[];
}

/**
 * Painel de seleção múltipla **sempre visível**: busca no topo, "Marcar todos /
 * Nenhum", e a lista de checkboxes rolando dentro da própria caixa. "Marcar
 * todos" e "Nenhum" agem sobre o que a busca está mostrando.
 */
export function painelSelecao(opts: {
  itens: readonly { id: string; nome: string }[];
  /** Ids já marcados ao abrir (padrão: nenhum). */
  marcadosPorPadrao?: readonly string[];
  buscaPlaceholder?: string;
  aoMudar?: () => void;
}): PainelSelecao {
  const marcados = new Set<string>(opts.marcadosPorPadrao ?? []);
  let termo = "";
  let devolverFoco = false;

  const painel = el("div", { class: "selecao-painel" });

  const filtrados = (): readonly { id: string; nome: string }[] =>
    termo.trim() === ""
      ? opts.itens
      : opts.itens.filter((i) => casaBusca(termo, i.nome));

  const redesenhar = (): void => {
    clear(painel);
    const lista = filtrados();

    const busca = el("input", {
      type: "search",
      class: "selecao-busca",
      placeholder: opts.buscaPlaceholder ?? "Buscar…",
      "aria-label": opts.buscaPlaceholder ?? "Buscar",
    }) as HTMLInputElement;
    busca.value = termo;
    busca.addEventListener("input", () => {
      termo = busca.value;
      devolverFoco = true;
      redesenhar();
    });

    const marcarTodos = botao("link", {}, "Marcar Todos");
    const nenhum = botao("link", {}, "Nenhum");
    marcarTodos.addEventListener("click", () => {
      for (const i of lista) marcados.add(i.id);
      redesenhar();
      opts.aoMudar?.();
    });
    nenhum.addEventListener("click", () => {
      for (const i of lista) marcados.delete(i.id);
      redesenhar();
      opts.aoMudar?.();
    });

    const corpo = el("div", { class: "selecao-lista" });
    if (lista.length === 0) {
      corpo.append(el("p", { class: "muted selecao-vazia" }, "Nenhum resultado."));
    }
    for (const i of lista) {
      const cb = el("input", { type: "checkbox" }) as HTMLInputElement;
      cb.checked = marcados.has(i.id);
      // só o `aoMudar` — sem redesenhar, para não perder a rolagem da lista
      cb.addEventListener("change", () => {
        if (cb.checked) marcados.add(i.id);
        else marcados.delete(i.id);
        opts.aoMudar?.();
      });
      corpo.append(el("label", { class: "ms-item" }, cb, ` ${i.nome}`));
    }

    painel.append(
      el("div", { class: "selecao-busca-linha" }, busca),
      el("div", { class: "ms-acoes" }, marcarTodos, nenhum),
      corpo,
    );

    if (devolverFoco) {
      devolverFoco = false;
      setTimeout(() => {
        busca.focus();
        const n = busca.value.length;
        busca.setSelectionRange(n, n);
      }, 0);
    }
  };
  redesenhar();

  return {
    element: painel,
    selecionados: () =>
      opts.itens.map((i) => i.id).filter((id) => marcados.has(id)),
  };
}
